import React, {useCallback, useEffect, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {useFragment, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {tokensForThemeProvider} from '@forecast-it/design-system';
import {D} from '../ai-chat/design-tokens';
import {PromptDropdown_viewer$key} from './__generated__/PromptDropdown_viewer.graphql';
import PromptRow from './PromptRow';
import AgentDropdown from './AgentDropdown';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import {CommitMutation} from '../../mutations/CommitMutation';
import DeleteSavedPromptMutation from '../../mutations/agent/DeleteSavedPromptMutation';

const {font} = tokensForThemeProvider.sky.sys;

const bodyFont = css`
	${font.body['1']}
`;

export const PromptDropdown_viewerFragment = graphql`
	fragment PromptDropdown_viewer on Viewer {
		savedPrompts(first: 100) @connection(key: "PromptDropdown_savedPrompts", filters: []) {
			edges {
				node {
					id
					promptText
					...PromptRow_savedPrompt
				}
			}
		}
	}
`;

const EmptyState = styled.p`
	${bodyFont}
	padding: 12px 16px;
	color: ${D.v2.textPrimary};
	margin: 0;
`;

const DROPDOWN_STYLE: React.CSSProperties = {
	top: 52,
	right: 52,
	width: 260,
	maxHeight: 320,
	overflowY: 'auto',
};

type PromptDropdownProps = {
	viewerRef: PromptDropdown_viewer$key | null;
	onClose: () => void;
	excludeRef?: React.RefObject<HTMLElement>;
};

const PromptDropdown = ({viewerRef, onClose, excludeRef}: PromptDropdownProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const {inputValue, setInputValue, setSavedPromptTexts} = useAgentDrawerContext();
	// viewerRef must be called unconditionally (Rules of Hooks), but Relay will
	// return null data when passed null, so we guard below before rendering.
	const viewer = useFragment(PromptDropdown_viewerFragment, viewerRef);

	const edges = viewer?.savedPrompts?.edges ?? [];

	const savedTexts = useMemo(() => {
		const texts = new Set(edges.map(e => e?.node?.promptText ?? '').filter(Boolean));
		return texts as ReadonlySet<string>;
	}, [edges]);

	// Keep the set of saved prompt texts in context so DrawerFooter can check
	// whether the current input is already saved.
	useEffect(() => {
		setSavedPromptTexts(savedTexts);
	}, [savedTexts, setSavedPromptTexts]);

	const handleSelect = useCallback(
		(promptText: string) => {
			setInputValue(promptText);
			onClose();
		},
		[setInputValue, onClose]
	);

	const handleDeletePrompt = useCallback((id: string) => {
		CommitMutation(DeleteSavedPromptMutation, {id});
	}, []);

	if (!viewer) return null;

	return (
		<AgentDropdown onClose={onClose} excludeRef={excludeRef} style={DROPDOWN_STYLE}>
			{edges.length === 0 ? (
				<EmptyState>{formatMessage({id: 'nova.prompt.empty_state'})}</EmptyState>
			) : (
				edges.map(edge => {
					if (!edge?.node) return null;
					const selected = !!edge.node.promptText && edge.node.promptText === inputValue;
					return (
						<PromptRow
							key={edge.node.id}
							savedPromptRef={edge.node}
							onSelect={handleSelect}
							onDelete={handleDeletePrompt}
							selected={selected}
						/>
					);
				})
			)}
		</AgentDropdown>
	);
};

export default PromptDropdown;
